import type { Carer } from '@lmc/ts-common'
import { Icon, IconType } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import dayjs from 'dayjs'
import type { CSSProperties, ReactNode } from 'react'
import styled from 'styled-components'
import { ProfilePicture } from '../generic'

export interface LastUpdateBoxProps {
  carer?: Carer | null
  children?: ReactNode
  comment?: string | null
  date?: string | null
  /** Grey outer + rounded inner strip (e.g. profile forms). Overrides default `lmc-card` shell. */
  formInset?: {
    outerStyle?: CSSProperties
    innerStyle?: CSSProperties
  }
  noDateText?: string
  noTitle?: boolean
  rightChildren?: ReactNode
  /** Applied to the `lmc-card` wrapper, or to the form-inset outer wrapper when `formInset` is set. */
  style?: CSSProperties
  title?: string
}

type DisplayProps = {
  carer?: Carer | null
  children?: ReactNode
  comment?: string | null
  displayDate: string
  displayTitle: string
  hasDate: boolean
  noTitle?: boolean
  rightChildren?: ReactNode
}

function getLastUpdateDisplay(
  date: string | null | undefined,
  noDateText: string | undefined,
  title: string | undefined
) {
  const hasDate = !!date
  const dateTime = date ? dayjs(date).format('DD-MM-YYYY HH:mm') : ''
  const displayTitle = title || 'Last updated'
  const displayNoDate = noDateText || 'No updates yet'
  const displayDate = hasDate ? dateTime : displayNoDate
  return { hasDate, displayTitle, displayDate }
}

function LastUpdateBoxContent({
  carer,
  children,
  comment,
  displayDate,
  displayTitle,
  hasDate,
  noTitle,
  rightChildren
}: DisplayProps) {
  return (
    <>
      <LastReviewRow>
        {!noTitle && <TitleText>{displayTitle}</TitleText>}
        <DateRow>
          <CalendarGlyph />
          <DateTextBlock>
            <Text level={1.5}>{displayDate}</Text>
          </DateTextBlock>
        </DateRow>
        {carer && hasDate && (
          <CarerRow>
            <CarerPictureWrap>
              <ProfilePicture
                id="carer-picture"
                url={carer.picture}
                type="handover__small"
              />
            </CarerPictureWrap>
            <CarerNameBlock>
              <Text level={1.5}>
                {carer.name.first} {carer.name.last}
              </Text>
            </CarerNameBlock>
          </CarerRow>
        )}
        {rightChildren}
      </LastReviewRow>
      {children}
      {comment ? (
        <CommentBlock>
          <CommentLabel>Comment</CommentLabel>
          <CommentBody>{comment}</CommentBody>
        </CommentBlock>
      ) : null}
    </>
  )
}

function LastUpdateBox({
  noTitle,
  carer,
  date,
  noDateText,
  style,
  title,
  rightChildren,
  comment,
  formInset,
  children
}: LastUpdateBoxProps) {
  const { hasDate, displayTitle, displayDate } = getLastUpdateDisplay(
    date,
    noDateText,
    title
  )

  const content = (
    <LastUpdateBoxContent
      carer={carer}
      // biome-ignore lint/correctness/noChildrenProp: <explanation>
      children={children}
      comment={comment}
      displayDate={displayDate}
      displayTitle={displayTitle}
      hasDate={hasDate}
      noTitle={noTitle}
      rightChildren={rightChildren}
    />
  )

  if (formInset) {
    return (
      <FormInsetOuter style={{ ...style, ...formInset.outerStyle }}>
        <FormInsetInner style={formInset.innerStyle}>{content}</FormInsetInner>
      </FormInsetOuter>
    )
  }

  return (
    <MainCard
      $withExtras={!!(rightChildren || children)}
      className="lmc-card"
      style={style}
    >
      {content}
    </MainCard>
  )
}

const CalendarGlyph = styled(Icon).attrs({
  customIcon: IconType.Calendar
})`
  width: 33px;
  height: 33px;
  flex-shrink: 0;
`

const CarerNameBlock = styled.div.attrs({ id: 'carer-name' })`
  align-self: center;
  padding-left: 10px;
`

const CarerPictureWrap = styled.div`
  align-self: center;
`

const CarerRow = styled.div`
  display: flex;
  margin: 3px 0;
`

const CommentBlock = styled.div`
  display: flex;
  align-items: flex-start;
  flex-flow: row nowrap;
  margin: 16px 0 10px;
  min-height: 20px;
`

const CommentBody = styled(Text).attrs({ level: 1.5 })`
  padding-left: 20px;
  white-space: pre-wrap;
`

const CommentLabel = styled(Text).attrs({ level: 1.5, weight: 'bold' })`
  display: block;
  align-self: flex-start;
  margin: 0 20px 0 0;
`

const DateRow = styled.div`
  display: flex;
  margin: 3px 50px 3px 0;
`

const DateTextBlock = styled.div.attrs({ id: 'last-review-date' })`
  display: block;
  align-self: center;
  color: ${({ theme }) => theme.color.neutral.color1};
  opacity: 0.7;
  padding-left: 15px;
`

const FormInsetInner = styled.div`
  background-color: ${({ theme }) => theme.color.neutral.color10};
  border-radius: 16px;
  padding: 8px 12px;
`

const FormInsetOuter = styled.div`
  box-sizing: border-box;
  margin-top: 8px;
  padding-inline: 0;
  width: 100%;
`

const LastReviewRow = styled.div`
  display: flex;
  flex-flow: row wrap;
  min-height: 42px;
`

const MainCard = styled.div<{ $withExtras: boolean }>`
  display: flex;
  align-content: ${({ $withExtras }) =>
    $withExtras ? 'initial' : 'space-between'};
  flex-flow: column wrap;
  padding: 5px 25px;
`

const TitleText = styled(Text).attrs({ level: 1.5, weight: 'bold' })`
  display: block;
  align-self: center;
  margin: 3px 20px 3px 0;
`

export default LastUpdateBox
